import { Button, Typography, Box } from '@mui/material';
import { BUSINESS_PACKAGE_CODES, PACKAGE_TYPE } from '@src/constants/package';
import {
  calculateRemainingResource,
  getMergePackageName,
  getPackageName,
} from '@src/services/package';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CONTACT_US_PAGE, VBEE_CONSOLE_URL } from '@src/configs';
import route from '@src/constants/route';
import useUserInfo from '@src/hooks/useTtsUser';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import PackageContainer from './components/PackageContainer';
import PackageV2 from './PackageV2';
import AutoRenewPackageInfo from './AutoRenewPackageInfo';

const DATE_FORMAT = 'DD/MM/YYYY - HH:mm';

const SubscriptionItemV2 = ({
  packageInfo = {},
  usingOrder = {},
  isFreePackage,
  isMobile,
  isApiPackage,
  preservationExpiryDate,
  subscriptionPurchase,
  lastUsingOrder,
  captureReferralCreditsOneTime,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { lockCharacters = 0 } = user;
  const { packageExpiryDate, paymentMethod } = usingOrder;
  const { type, code } = packageInfo;
  const { type: paymentMethodType } = paymentMethod || {};
  const { packageExpiryDate: lastOrderPackageExpiryDate } =
    lastUsingOrder || {};

  useEffect(() => {
    const fetchUser = async () => {
      const userRes = await apis.account.getMe();
      dispatch(actions.auth.updateUserInfo(userRes.result));
    };
    fetchUser();
  }, [dispatch]);

  const isLockedCustom =
    usingOrder.type === PACKAGE_TYPE.STUDIO &&
    ttsUser?.studio?.custom?.remainingCredits &&
    ttsUser?.studio?.custom?.isLocked;

  const hasExpired =
    packageExpiryDate && moment(packageExpiryDate).isBefore(moment());
  const isLocked = (hasExpired && !!lockCharacters) || isLockedCustom;
  const retentionDate = moment(preservationExpiryDate).format(DATE_FORMAT);
  const isBusinessPackage = BUSINESS_PACKAGE_CODES.includes(code);
  const hasPreservationExpired =
    preservationExpiryDate && moment(preservationExpiryDate).isBefore(moment());
  const lastOrderHasExpired =
    lastOrderPackageExpiryDate &&
    moment(lastOrderPackageExpiryDate).isBefore(moment());
  const hasLockCharactersLastOrder = lastOrderHasExpired && !!lockCharacters;

  const handleRedirectToBuyPackage = () => {
    let redirectUrl = `${route.PAYMENT}`;
    if (type === PACKAGE_TYPE.API) {
      redirectUrl = `${VBEE_CONSOLE_URL}/payments?package=${code}`;
      window.open(redirectUrl);
    } else {
      history.push(redirectUrl);
    }
  };

  const handleClickContactSupport = () =>
    window.open(CONTACT_US_PAGE, '_blank', 'noopener,noreferrer');

  const renderPackageButton = () => {
    if (isBusinessPackage) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          data-id="button-buy"
          data-current-package-code={code}
          onClick={handleClickContactSupport}
        >
          {t('contact')}
        </Button>
      );
    }

    if (isApiPackage) {
      return (
        <Button
          fullWidth
          className="buy-more-btn"
          data-id="button-buy"
          data-current-package-code={code}
          onClick={handleRedirectToBuyPackage}
        >
          {t('buyMore')}
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        className="buy-more-btn"
        data-id="button-buy"
        data-current-package-code={code}
        onClick={handleRedirectToBuyPackage}
      >
        {t('buyNow')}
      </Button>
    );
  };

  const remainingResource = calculateRemainingResource(type, user, hasExpired);

  const renderDescription = () => {
    const hasLockCharacters = hasExpired && !!lockCharacters;
    if (hasLockCharacters) {
      return (
        <Box className="package-retention-text-container">
          <Typography className="package-retention-text">
            {t('characterRetentionTime')}
          </Typography>
          <Typography className="package-retention-date">
            {retentionDate}
          </Typography>
        </Box>
      );
    }

    return null;
  };
  return (
    <PackageContainer
      packageName={getPackageName(
        packageInfo?.name,
        packageInfo?.code,
        language,
        t,
        true,
        true,
      )}
      productType={t(`${getMergePackageName(type)}`)}
      renderDescription={renderDescription}
      renderButton={renderPackageButton}
      packageDetail={
        <>
          <PackageV2
            isLocked={isLocked}
            credits={remainingResource}
            packageExpiryDate={packageExpiryDate}
            isMobile={isMobile}
            hasPreservationExpired={hasPreservationExpired}
            isFreePackage={isFreePackage}
            isApiPackage={isApiPackage}
            referralCredits={captureReferralCreditsOneTime}
          />
          {!hasExpired && (
            <AutoRenewPackageInfo
              packageExpiryDate={packageExpiryDate}
              subscriptionPurchase={subscriptionPurchase}
              packageInfo={packageInfo}
              paymentMethodType={paymentMethodType}
              orderInfo={usingOrder}
            />
          )}
        </>
      }
      isMobile={isMobile}
      packageNameLastOrder={getPackageName(
        lastUsingOrder?.package?.name,
        lastUsingOrder?.package?.code,
        language,
        t,
        true,
        true,
      )}
      isFreePackage={isFreePackage}
      hasLockCharactersLastOrder={hasLockCharactersLastOrder}
      lastOrderPackageExpiryDate={lastOrderPackageExpiryDate}
      preservationExpiryDate={preservationExpiryDate}
      lockCharacters={lockCharacters}
    />
  );
};

export default SubscriptionItemV2;
